<template>
  <div :class="[bgClass, loaderClass, 'relative overflow-hidden']">
    <div
      class="shimmer absolute top-0 right-0 bottom-0 left-0"
      :style="shimmerStyle"
    />
    <slot />
  </div>
</template>

<script lang="ts">
export enum SkeletonLoaderTypes {
  rectangle = 'rectangle',
  circle = 'circle',
}

const LOADER_CSS_CLASSES = {
  [SkeletonLoaderTypes.rectangle]: 'rounded',
  [SkeletonLoaderTypes.circle]: 'rounded-full',
};

type LoaderTypesKeys = keyof typeof SkeletonLoaderTypes;
type LoaderTypesValues = (typeof SkeletonLoaderTypes)[LoaderTypesKeys];

const SHIMMER_COLOR = '#ffffff';

const isHexColor = (hexColor: string) => {
  const hex = hexColor.replace('#', '');

  return typeof hexColor === 'string' && hexColor.startsWith('#') && hex.length === 6 && !isNaN(Number('0x' + hex));
};

const hexToRgb = (hex: string) => `${hex.match(/\w\w/g)?.map(x => +`0x${x}`)}`;
</script>

<script setup lang="ts">
import { computed, toRefs } from 'vue';
interface Props {
  type?: LoaderTypesValues;
  bgClass?: string;
  cssClass?: string;
  shimmerColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: SkeletonLoaderTypes.rectangle,
  bgClass: 'bg-gray-300',
  cssClass: '',
  shimmerColor: SHIMMER_COLOR,
});

const { type, bgClass, cssClass, shimmerColor } = toRefs(props);

const shimmerStyle = computed(() => {
  const rgb = isHexColor(shimmerColor.value) ? hexToRgb(shimmerColor.value) : SHIMMER_COLOR;

  return {
    backgroundImage: `linear-gradient(90deg, rgba(${rgb}, 0) 0%, rgba(${rgb}, 0.2) 20%, rgba(${rgb}, 0.5) 60%, rgba(${rgb}, 0))`,
  };
});

const loaderClass = computed(() => {
  if (cssClass.value) {
    return cssClass.value;
  } else {
    const typeValue = type.value.toLowerCase() as LoaderTypesKeys;
    return LOADER_CSS_CLASSES[typeValue];
  }
});
</script>
